import type { ComponentType } from "react";
import dynamic from "next/dynamic";
import { type SanityPageBuilderSection, type SanityPageBuilderSectionContent } from "@ouster/shared";
import type { SanityLiveSection } from "~/features/sanity/live/types";
import { SanityRichText } from "~/features/sanity/rich-text";
import { isEmptyLegacyEditorialCardSection } from "./legacy-section";
import { SanityAccordionListSection } from "./sections/accordion-list";
import { SanityBannerSection } from "./sections/banner";
import { SanityBenefitsSection } from "./sections/benefits";
import { SanityBenefitsListSection } from "./sections/benefits-list";
import { SanityCallToActionListSection } from "./sections/call-to-action-list";
import { SanityCallToActionCardSection } from "./sections/call-to-action-card";
import { SanityIntroHeadlineBSection } from "./sections/intro-headline-b";
import { SanityGridCardsSection } from "./sections/grid-cards";
import { SanityHighlightedProductsSection } from "./sections/highlighted-products";
import { SanityLogoWallSection } from "./sections/logo-wall";
import { SanityExternalProductsSection } from "./sections/external-products";
import { SanityFaqSection } from "./sections/faq";
import { SanityGraphicCardsSection } from "./sections/graphic-cards";
import { SanityGraphicStatsSection } from "./sections/graphic-stats";
import { SanityHeadlineSection } from "./sections/headline";
import { SanityHighlightsSection } from "./sections/highlights";
import { SanityInfoCardsSection } from "./sections/info-cards";
import { SanityMediaCardsSection } from "./sections/media-cards";
import { SanityOusterMediaGridSection } from "./sections/ouster-media-grid";
import { SanityProductDownloadsTableSection } from "./sections/product-downloads-table";
import { SanityProductRowSection } from "./sections/product-row";
import { SanityShopGridSection } from "./sections/shop-grid";
import { SanityShopHeroSection } from "./sections/shop-hero";
import { SanityShopProductSection } from "./sections/shop-product";
import { SanityShopStatementSection } from "./sections/shop-statement";
import { SanityNewsletterSection } from "./sections/newsletter";
import { SanitySpecsListSection } from "./sections/specs-list";
import { SanitySpecsRowSection } from "./sections/specs-row";
import { SanityTextFactsMediaSection } from "./sections/text-facts-media";
import { SanityTextMedia12Section } from "./sections/text-media1-2";
import { SanityTextMedia13Section } from "./sections/text-media1-3";
import { SanityTextMedia13BSection } from "./sections/text-media1-3-b";
import { SanityTextMediaCSection } from "./sections/text-media-c";
import { SanityToolCardsSection } from "./sections/tool-cards";
import { SanityTwoColumnMediaSection } from "./sections/two-column-media";
import { SanityHeadlineBSection } from "./sections/headline-b";
import { SanityHeadlineDSection } from "./sections/headline-d";
import { SanityHighlightHeadlineSection } from "./sections/highlight-headline";
import { SanityIntroHeadlineSection } from "./sections/intro-headline";
import { SanityIntroDetailsSection } from "./sections/intro-details";
import { SanityLinkBarsSection } from "./sections/link-bars";
import { SanityLinkListSection } from "./sections/link-list";
import { SanityMediaOnlySection } from "./sections/media-only";
import { SanityMediaSliderSection } from "./sections/media-slider";
import { SanityQuoteSection } from "./sections/quote";
import { SanitySpacerSection } from "./sections/spacer";
import { SanitySocialShareSection } from "./sections/social-share";
import { SanityTextOnlySection } from "./sections/text-only";
type SectionComponent = ComponentType<{
  section: SanityPageBuilderRenderSection & {
    sectionContent: SanityPageBuilderSectionContent;
  };
}>;
const SanityBannerNotesSection = dynamic(() => import("./sections/banner-notes").then(mod => mod.SanityBannerNotesSection as unknown as SectionComponent)) as SectionComponent;
const SanityChipEvolutionGraphSection = dynamic(() => import("./sections/chip-evolution-graph").then(mod => mod.SanityChipEvolutionGraphSection as unknown as SectionComponent)) as SectionComponent;
const SanityCubeExperienceSection = dynamic(() => import("./sections/cube-experience").then(mod => mod.SanityCubeExperienceSection as unknown as SectionComponent)) as SectionComponent;
const SanityContentGallerySection = dynamic(() => import("./sections/content-gallery").then(mod => mod.SanityContentGallerySection as unknown as SectionComponent)) as SectionComponent;
const SanityCoreFeaturesGallerySection = dynamic(() => import("./sections/core-features-gallery").then(mod => mod.SanityCoreFeaturesGallerySection as unknown as SectionComponent)) as SectionComponent;
const SanityCoreVideoFeaturesGallerySection = dynamic(() => import("./sections/core-video-features-gallery").then(mod => mod.SanityCoreVideoFeaturesGallerySection as unknown as SectionComponent)) as SectionComponent;
const SanityDeepDiveGallerySection = dynamic(() => import("./sections/deep-dive-gallery").then(mod => mod.SanityDeepDiveGallerySection as unknown as SectionComponent)) as SectionComponent;
const SanityDistributorsSection = dynamic(() => import("./sections/distributors").then(mod => mod.SanityDistributorsSection as unknown as SectionComponent)) as SectionComponent;
const SanityEditorialCardsSection = dynamic(() => import("./sections/editorial-cards").then(mod => mod.SanityEditorialCardsSection as unknown as SectionComponent)) as SectionComponent;
const SanityLegacyEditorialCardsArticleCardSection = dynamic(() => import("./sections/legacy-editorial-cards-article-card").then(mod => mod.SanityLegacyEditorialCardsArticleCardSection as unknown as SectionComponent)) as SectionComponent;
const SanityFeaturedGallerySection = dynamic(() => import("./sections/featured-gallery").then(mod => mod.SanityFeaturedGallerySection as unknown as SectionComponent)) as SectionComponent;
const SanityFeaturesComparisonTableSection = dynamic(() => import("./sections/features-comparison-table").then(mod => mod.SanityFeaturesComparisonTableSection as unknown as SectionComponent)) as SectionComponent;
const SanityGallerySection = dynamic(() => import("./sections/gallery").then(mod => mod.SanityGallerySection as unknown as SectionComponent)) as SectionComponent;
const SanityHardwareComparisonTableSection = dynamic(() => import("./sections/hardware-comparison-table").then(mod => mod.SanityHardwareComparisonTableSection as unknown as SectionComponent)) as SectionComponent;
const SanityHardwareComparisonTableV2Section = dynamic(() => import("./sections/hardware-comparison-table-v2").then(mod => mod.SanityHardwareComparisonTableV2Section as unknown as SectionComponent)) as SectionComponent;
const SanityHighlightedCardsSection = dynamic(() => import("./sections/highlighted-cards").then(mod => mod.SanityHighlightedCardsSection as unknown as SectionComponent)) as SectionComponent;
const SanityHighlightHeadlinesSection = dynamic(() => import("./sections/highlight-headlines").then(mod => mod.SanityHighlightHeadlinesSection as unknown as SectionComponent)) as SectionComponent;
const SanityIframeSection = dynamic(() => import("./sections/iframe").then(mod => mod.SanityIframeSection as unknown as SectionComponent)) as SectionComponent;
const SanityLogoSliderSection = dynamic(() => import("./sections/logo-slider").then(mod => mod.SanityLogoSliderSection as unknown as SectionComponent)) as SectionComponent;
const SanityMediaCarouselSection = dynamic(() => import("./sections/media-carousel").then(mod => mod.SanityMediaCarouselSection as unknown as SectionComponent)) as SectionComponent;
const SanityMediaHighlightsSection = dynamic(() => import("./sections/media-highlights").then(mod => mod.SanityMediaHighlightsSection as unknown as SectionComponent)) as SectionComponent;
const SanityMediaWithHighlightsSection = dynamic(() => import("./sections/media-with-highlights").then(mod => mod.SanityMediaWithHighlightsSection as unknown as SectionComponent)) as SectionComponent;
const SanityMarketoEmbedFormSection = dynamic(() => import("./sections/marketo-embed-form").then(mod => mod.SanityMarketoEmbedFormSection as unknown as SectionComponent)) as SectionComponent;
const SanityMarketoFormSection = dynamic(() => import("./sections/marketo-form").then(mod => mod.SanityMarketoFormSection as unknown as SectionComponent)) as SectionComponent;
const SanityCustomerIoFormSection = dynamic(() => import("./sections/customer-io-form").then(mod => mod.SanityCustomerIoFormSection as unknown as SectionComponent)) as SectionComponent;
const SanityOfficesMapSection = dynamic(() => import("./sections/offices-map").then(mod => mod.SanityOfficesMapSection as unknown as SectionComponent)) as SectionComponent;
const SanityOpenJobPositionsSection = dynamic(() => import("./sections/open-job-positions").then(mod => mod.SanityOpenJobPositionsSection as unknown as SectionComponent)) as SectionComponent;
const SanityPartnerGallerySection = dynamic(() => import("./sections/partner-gallery").then(mod => mod.SanityPartnerGallerySection as unknown as SectionComponent)) as SectionComponent;
const SanityPartnersSection = dynamic(() => import("./sections/partners").then(mod => mod.SanityPartnersSection as unknown as SectionComponent)) as SectionComponent;
const SanityPointCloudSection = dynamic(() => import("./sections/point-cloud").then(mod => mod.SanityPointCloudSection as unknown as SectionComponent)) as SectionComponent;
const SanityProductSpecsSection = dynamic(() => import("./sections/product-specs").then(mod => mod.SanityProductSpecsSection as unknown as SectionComponent)) as SectionComponent;
const SanityRgbSection = dynamic(() => import("./sections/rgb").then(mod => mod.SanityRgbSection as unknown as SectionComponent)) as SectionComponent;
const SanityScrollFeaturesSection = dynamic(() => import("./sections/scroll-features").then(mod => mod.SanityScrollFeaturesSection as unknown as SectionComponent)) as SectionComponent;
const SanityScrollHeadlinesSection = dynamic(() => import("./sections/scroll-headlines").then(mod => mod.SanityScrollHeadlinesSection as unknown as SectionComponent)) as SectionComponent;
const SanityScrollSpecsSection = dynamic(() => import("./sections/scroll-specs").then(mod => mod.SanityScrollSpecsSection as unknown as SectionComponent)) as SectionComponent;
const SanitySensorFlowGraphicsSection = dynamic(() => import("./sections/sensor-flow-graphics").then(mod => mod.SanitySensorFlowGraphicsSection as unknown as SectionComponent)) as SectionComponent;
const SanityStackedGallerySection = dynamic(() => import("./sections/stacked-gallery").then(mod => mod.SanityStackedGallerySection as unknown as SectionComponent)) as SectionComponent;
const SanityTechnicalComparisonSection = dynamic(() => import("./sections/technical-comparison").then(mod => mod.SanityTechnicalComparisonSection as unknown as SectionComponent)) as SectionComponent;
const SanityTestimonialCarouselSection = dynamic(() => import("./sections/testimonial-carousel").then(mod => mod.SanityTestimonialCarouselSection as unknown as SectionComponent)) as SectionComponent;
export type SanityPageBuilderRenderSection = SanityPageBuilderSection & {
  loaded?: boolean;
  /** Present only under preview; drives the client live-update subscription. */
  live?: SanityLiveSection;
};
type Props = {
  sections?: SanityPageBuilderRenderSection[];
};
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}
function getStringField(content: SanityPageBuilderSectionContent | undefined, field: string) {
  if (!isRecord(content)) {
    return undefined;
  }
  const value = (content as Record<string, unknown>)[field];
  return typeof value === "string" ? value : undefined;
}
function getRichTextField(content: SanityPageBuilderSectionContent | undefined, field: string) {
  if (!isRecord(content)) {
    return undefined;
  }
  const value = (content as Record<string, unknown>)[field];
  return Array.isArray(value) ? value : undefined;
}
function getSectionLabel(section: SanityPageBuilderRenderSection) {
  return section.sectionSettings?.sectionTitle || section.sectionContent?._type || section._type || "Sanity section";
}
function SanityPageBuilderSectionScaffold({
  section
}: {
  section: SanityPageBuilderRenderSection;
}) {
  const content = section.sectionContent;
  const headline = getStringField(content, "headline") || getStringField(content, "title");
  const eyebrow = getStringField(content, "eyebrow");
  const richText = getRichTextField(content, "text");
  const text = getStringField(content, "text");
  return <section id={section.sectionSettings?.sectionId?.current || section.sectionSettings?.anchorName || section._key} data-sanity-section-key={section._key} data-sanity-section-type={section._type} style={{
    borderTop: section.sectionSettings?.hasTopDivider ? "1px solid rgba(0,0,0,0.12)" : undefined,
    padding: "clamp(48px, 8vw, 96px) clamp(24px, 6vw, 72px)"
  }}>
      <div style={{
      margin: "0 auto",
      maxWidth: 980
    }}>
        <p style={{
        color: "#60605a",
        fontSize: 12,
        letterSpacing: 0,
        margin: "0 0 12px",
        textTransform: "uppercase"
      }}>
          {getSectionLabel(section)}
        </p>
        {eyebrow ? <p style={{
        color: "#3573e8",
        margin: "0 0 12px"
      }}>{eyebrow}</p> : null}
        {headline ? <h2 style={{
        fontSize: "clamp(32px, 5vw, 56px)",
        fontWeight: 400,
        lineHeight: 1.05,
        margin: "0 0 24px"
      }}>
            {headline}
          </h2> : null}
        {richText ? <SanityRichText value={richText} /> : null}
        {!richText && text ? <p style={{
        fontSize: 18,
        lineHeight: 1.5,
        margin: 0
      }}>{text}</p> : null}
        {!headline && !richText && !text ? <p style={{
        color: "#60605a",
        fontSize: 14,
        lineHeight: 1.5,
        margin: 0
      }}>
            {section.loaded ? "Section data loaded; frontend component not wired yet." : "Section loader returned no data."}
          </p> : null}
      </div>
    </section>;
}
const SECTION_COMPONENTS: Record<string, SectionComponent> = {
  "wild.pageBuilder.section.textOnly": SanityTextOnlySection as SectionComponent,
  "wild.pageBuilder.section.quote": SanityQuoteSection as SectionComponent,
  "wild.pageBuilder.section.mediaOnly": SanityMediaOnlySection as SectionComponent,
  "wild.pageBuilder.section.mediaSlider": SanityMediaSliderSection as SectionComponent,
  "ouster.pageBuilder.section.externalProducts": SanityExternalProductsSection as SectionComponent,
  "ouster.pageBuilder.section.spacer": SanitySpacerSection as SectionComponent,
  "ouster.pageBuilder.section.socialShare": SanitySocialShareSection as SectionComponent,
  "ouster.pageBuilder.section.headline": SanityHeadlineSection as SectionComponent,
  "ouster.pageBuilder.section.headline_B": SanityHeadlineBSection as SectionComponent,
  "ouster.pageBuilder.section.headline_D": SanityHeadlineDSection as SectionComponent,
  "ouster.pageBuilder.section.textMedia1_2": SanityTextMedia12Section as SectionComponent,
  "ouster.pageBuilder.section.textMedia1_3": SanityTextMedia13Section as SectionComponent,
  "ouster.pageBuilder.section.textMedia1_3_B": SanityTextMedia13BSection as SectionComponent,
  "ouster.pageBuilder.section.textMediaC": SanityTextMediaCSection as SectionComponent,
  "ouster.pageBuilder.section.specsList": SanitySpecsListSection as SectionComponent,
  "ouster.pageBuilder.section.twoColumnMedia": SanityTwoColumnMediaSection as SectionComponent,
  "ouster.pageBuilder.section.textFactsMedia": SanityTextFactsMediaSection as SectionComponent,
  "ouster.pageBuilder.section.stackedGallery": SanityStackedGallerySection,
  "ouster.pageBuilder.section.mediaCards": SanityMediaCardsSection as SectionComponent,
  "ouster.pageBuilder.section.highlights": SanityHighlightsSection as SectionComponent,
  "ouster.pageBuilder.section.specsRow": SanitySpecsRowSection as SectionComponent,
  "ouster.pageBuilder.section.partnerGallery": SanityPartnerGallerySection,
  "ouster.pageBuilder.section.editorialCards": SanityEditorialCardsSection,
  editorialCardsArticleCard: SanityLegacyEditorialCardsArticleCardSection,
  "ouster.pageBuilder.section.featuredGallery": SanityFeaturedGallerySection,
  "ouster.pageBuilder.section.productRow": SanityProductRowSection as SectionComponent,
  "ouster.pageBuilder.section.shopGrid": SanityShopGridSection as SectionComponent,
  "ouster.pageBuilder.section.shopHero": SanityShopHeroSection as SectionComponent,
  "ouster.pageBuilder.section.shopProduct": SanityShopProductSection as SectionComponent,
  "ouster.pageBuilder.section.shopStatement": SanityShopStatementSection as SectionComponent,
  "ouster.pageBuilder.section.highlightedProducts": SanityHighlightedProductsSection as SectionComponent,
  "ouster.pageBuilder.section.hardwareComparisonTable": SanityHardwareComparisonTableSection,
  "ouster.pageBuilder.section.hardwareComparisonTableV2": SanityHardwareComparisonTableV2Section,
  "ouster.pageBuilder.section.partners": SanityPartnersSection,
  "ouster.pageBuilder.section.officesMap": SanityOfficesMapSection,
  "ouster.pageBuilder.section.distributors": SanityDistributorsSection,
  "ouster.pageBuilder.section.openJobPositions": SanityOpenJobPositionsSection,
  "ouster.pageBuilder.section.pointCloud": SanityPointCloudSection,
  "ouster.pageBuilder.section.chipEvolutionGraph": SanityChipEvolutionGraphSection,
  "ouster.pageBuilder.section.cubeExperience": SanityCubeExperienceSection,
  "ouster.pageBuilder.section.marketoForm": SanityMarketoFormSection,
  "ouster.pageBuilder.section.marketoEmbedForm": SanityMarketoEmbedFormSection,
  "ouster.pageBuilder.section.customerIoForm": SanityCustomerIoFormSection,
  "ouster.pageBuilder.section.highlightedCards": SanityHighlightedCardsSection,
  "ouster.pageBuilder.section.iframe": SanityIframeSection,
  "ouster.pageBuilder.section.rgb": SanityRgbSection,
  "ouster.pageBuilder.section.sensorFlowGraphics": SanitySensorFlowGraphicsSection,
  "ouster.pageBuilder.section.featuresComparisonTable": SanityFeaturesComparisonTableSection,
  "ouster.pageBuilder.section.technicalComparison": SanityTechnicalComparisonSection,
  "ouster.pageBuilder.section.scrollHeadlines": SanityScrollHeadlinesSection,
  "ouster.pageBuilder.section.scrollFeatures": SanityScrollFeaturesSection,
  "ouster.pageBuilder.section.scrollSpecs": SanityScrollSpecsSection,
  "ouster.pageBuilder.section.coreFeaturesGallery": SanityCoreFeaturesGallerySection,
  "ouster.pageBuilder.section.mediaHighlights": SanityMediaHighlightsSection,
  "ouster.pageBuilder.section.productSpecs": SanityProductSpecsSection,
  "ouster.pageBuilder.section.coreVideoFeaturesGallery": SanityCoreVideoFeaturesGallerySection,
  "ouster.pageBuilder.section.callToActionList": SanityCallToActionListSection as SectionComponent,
  "ouster.pageBuilder.section.productDownloadsTable": SanityProductDownloadsTableSection as SectionComponent,
  "ouster.pageBuilder.section.graphicCards": SanityGraphicCardsSection as SectionComponent,
  "ouster.pageBuilder.section.gallery": SanityGallerySection,
  "ouster.pageBuilder.section.contentGallery": SanityContentGallerySection,
  "ouster.pageBuilder.section.mediaGrid": SanityOusterMediaGridSection as SectionComponent,
  "ouster.pageBuilder.section.testimonialCarousel": SanityTestimonialCarouselSection,
  "ouster.pageBuilder.section.accordionList": SanityAccordionListSection as SectionComponent,
  "ouster.pageBuilder.section.mediaCarousel": SanityMediaCarouselSection,
  "ouster.pageBuilder.section.logoSlider": SanityLogoSliderSection,
  "ouster.pageBuilder.section.deepDiveGallery": SanityDeepDiveGallerySection,
  "ouster.pageBuilder.section.banner": SanityBannerSection as SectionComponent,
  "ouster.pageBuilder.section.bannerNotes": SanityBannerNotesSection,
  "ouster.pageBuilder.section.logoWall": SanityLogoWallSection as SectionComponent,
  "ouster.pageBuilder.section.gridCards": SanityGridCardsSection as SectionComponent,
  "ouster.pageBuilder.section.newsletter": SanityNewsletterSection as SectionComponent,
  "ouster.pageBuilder.section.highlightHeadlines": SanityHighlightHeadlinesSection,
  "ouster.pageBuilder.section.mediaWithHighlights": SanityMediaWithHighlightsSection,
  "ouster.pageBuilder.section.graphicStats": SanityGraphicStatsSection as SectionComponent,
  "ouster.pageBuilder.section.infoCards": SanityInfoCardsSection as SectionComponent,
  "ouster.pageBuilder.section.toolCards": SanityToolCardsSection as SectionComponent,
  "ouster.pageBuilder.section.benefits": SanityBenefitsSection as SectionComponent,
  "ouster.pageBuilder.section.benefitsList": SanityBenefitsListSection as SectionComponent,
  "ouster.pageBuilder.section.faq": SanityFaqSection as SectionComponent,
  "ouster.pageBuilder.section.highlightHeadline": SanityHighlightHeadlineSection as SectionComponent,
  "ouster.pageBuilder.section.introHeadline": SanityIntroHeadlineSection as SectionComponent,
  "ouster.pageBuilder.section.introHeadline_B": SanityIntroHeadlineBSection as SectionComponent,
  "ouster.pageBuilder.section.callToActionCard": SanityCallToActionCardSection as SectionComponent,
  "ouster.pageBuilder.section.introDetails": SanityIntroDetailsSection as SectionComponent,
  "ouster.pageBuilder.section.linkList": SanityLinkListSection as SectionComponent,
  "ouster.pageBuilder.section.linkBars": SanityLinkBarsSection as SectionComponent
};
const SECTION_TYPE_ALIASES: Record<string, string> = {
  "ouster.pageBuilder.section.graphicCards.field": "ouster.pageBuilder.section.graphicCards",
  "editorialCardsArticleCard.field": "editorialCardsArticleCard"
};
function getSectionContentType(section: SanityPageBuilderRenderSection) {
  return section.sectionContent?._type || SECTION_TYPE_ALIASES[section._type || ""];
}
export function SanityPageBuilderSection({
  section
}: {
  section: SanityPageBuilderRenderSection;
}) {
  if (isEmptyLegacyEditorialCardSection(section)) {
    return null;
  }
  const contentType = getSectionContentType(section);
  const Component = contentType ? SECTION_COMPONENTS[contentType] : undefined;
  const sectionContent = section.sectionContent;
  const spacingBelow = section.sectionSettings?.spacingBelow;
  const rendered = !Component || !sectionContent ? <SanityPageBuilderSectionScaffold section={section} /> : <Component section={{
    ...section,
    sectionContent
  }} />;

  // spacingBelow lets editors add / remove a spacer-sized gap after any
  // section without having to insert a dedicated `spacer` pageBuilder section.
  // Sizing matches the spacer block's `h-3xl` — see features/blocks/inventory/spacer.
  if (spacingBelow === "more") {
    return <>
        {rendered}
        <div data-spacing-below="more" style={{
        height: "var(--spacing-3xl)"
      }} />
      </>;
  }
  return rendered;
}
export function SanityPageBuilder({
  sections = []
}: Props) {
  if (!sections.length) {
    return null;
  }
  return <>
      {sections.map((section, index) => <SanityPageBuilderSection key={section._key || `${section._type}-${index}`} section={section} />)}
    </>;
}